/**
 * useActivationLandingData — le contexte du lien signé, et les deux effets qui l'accompagnent.
 *
 * Patron B : `getActivationLinkContext` répond un `JsonResponse` nu qui n'atteint jamais
 * `state.data`, donc la charge est tenue ici. L'erreur, elle, reste sur la tranche api : c'est le
 * view-model qui la lit pour choisir entre « lien invalide » et « panne passagère ».
 *
 * La balise de LECTURE (`markActivationViewedFromLink`) part d'ici, une fois le contexte chargé
 * AVEC une reprise à montrer : un scanner de mail qui précharge le lien fait un GET et n'exécute
 * rien, il ne peut donc pas éteindre les rappels J-5 / J-3 à la place de la famille.
 */
import { useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import {
    activationLandingState,
    type ApiActivationLinkContext,
} from "../../../../adapters/parent/Activation/activationAdapter"
import { useApiFetch } from "../../../hooks/useApiFetch"
import { type ActivationLinkParams } from "./activationLink"

export function useActivationLandingData(linkParams: ActivationLinkParams | null) {
    const dispatch = useDispatch()
    /** Fire-and-forget, une fois par montage — comme l'effet `[]` du legacy. */
    const viewedSentRef = useRef(false)

    const { raw } = useApiFetch<ApiActivationLinkContext>({
        service: subscriptionService.getActivationLinkContext,
        urlParams: linkParams ?? {},
        // Les paramètres sont stables pour une route donnée : la clé résume leur identité.
        requestKey: linkParams ? `${linkParams.u}:${linkParams.sig}` : null,
        enabled: linkParams != null,
        idleOnUnmount: [subscriptionService.getActivationLinkContext, subscriptionService.markActivationViewedFromLink],
        onSuccess: (data) => {
            if (data) amplitude.track("activation_link.landing_viewed", { state: activationLandingState(data) })
        },
    })

    useEffect(() => {
        if (!raw?.upcoming_activation?.planned || viewedSentRef.current || !linkParams) return
        viewedSentRef.current = true
        dispatch(apiRequest(subscriptionService.markActivationViewedFromLink, {}, {}, linkParams) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, raw])

    return { raw }
}
